// src/pages/Dashboard.jsx — Premium Overview
import { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../lib/authFetch';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import {
  RefreshCw, CheckCircle, AlertCircle, ChevronRight, Zap,
  Settings, X, FlaskConical, BarChart3, ShoppingCart,
  TrendingUp, Activity, Clock, Database, Megaphone,
  ArrowUpRight, ArrowDownRight, ShieldCheck, ShieldAlert, Ban,
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import toast from 'react-hot-toast';
import { useVisibilityRefresh } from '../hooks/useVisibilityRefresh';
import { useFetchUsage } from '../hooks/useFetchUsage';
import { SkeletonDashboard } from '../components/ui/Skeleton';

const card = 'bg-card rounded-2xl border border-border shadow-sm';

function StatusDot({ connected }) {
  return connected ? (
    <span className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 px-2.5 py-1 rounded-full">
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
      Live
    </span>
  ) : (
    <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground/70 bg-muted/40 px-2.5 py-1 rounded-full">
      <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40" />
      Off
    </span>
  );
}

function MetricCard({ label, value, sub, gradient, icon: Icon, href, badge, cta }) {
  const inner = (
    <div className={`${card} p-5 hover:shadow-md transition-all duration-200 group relative overflow-hidden h-full flex flex-col`}>
      <div className="flex items-start justify-between mb-4">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">{label}</p>
        <div className={`p-2 rounded-xl bg-gradient-to-br ${gradient} shadow-sm flex-shrink-0`}>
          <Icon className="h-4 w-4 text-white" />
        </div>
      </div>
      <div className="flex-1">
        <p className="text-3xl font-bold tracking-tight text-foreground mb-1">{value}</p>
        {sub && <p className="text-xs text-muted-foreground/70">{sub}</p>}
      </div>
      {badge && (
        <div className="mt-2 inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-ping" />
          {badge}
        </div>
      )}
      {href && (
        <div className="mt-3 flex items-center gap-1 text-xs font-semibold text-muted-foreground/70 group-hover:text-emerald-600 transition-colors">
          {cta || 'View dashboard'} <ChevronRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
        </div>
      )}
    </div>
  );
  return href ? <Link to={href}>{inner}</Link> : inner;
}

function ChartTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card text-white text-xs rounded-xl px-3 py-2 shadow-xl">
      <p className="font-semibold mb-1 text-muted-foreground/50">{label}</p>
      {payload.map(p => (
        <p key={p.name} style={{ color: p.color }} className="font-medium">
          {p.name}: ${Number(p.value || 0).toFixed(2)}
        </p>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const { user, profile, isPro, isTrialActive, daysRemaining } = useAuth();

  const [accounts, setAccounts] = useState({
    adsense:   { connected: false, lastFetch: null },
    admanager: { connected: false, name: '', lastFetch: null },
    metaAds:   { connected: false, count: 0, lastFetch: null },
    googleAds: { connected: false, count: 0, lastFetch: null },
    shopify:   { connected: false, count: 0, lastFetch: null },
    tiktokAds: { connected: false, count: 0, lastFetch: null },
  });
  const [announcements, setAnnouncements]   = useState([]);
  const [fetchingAll, setFetchingAll]       = useState(false);
  const [fetchProgress, setFetchProgress]   = useState(null);
  const [loading, setLoading]               = useState(true);
  const [todayRoas, setTodayRoas]           = useState({ publisher: { revenue: 0 }, ecommerce: { revenue: 0, spend: 0, roas: 0 } });
  const [hgStats, setHgStats]               = useState(null); // HapiGuard today stats
  const [activeLandingPages, setActiveLandingPages] = useState(0);
  const [activeTests, setActiveTests]       = useState(0);
  const [trendData, setTrendData]           = useState([]);
  // Fetch usage is read via the shared hook so Overview / Publisher /
  // Ecommerce all reflect the same counter.
  const { used: fetchUsedShared, limit: fetchLimitShared, remaining: fetchRemainingShared,
          lastFetchAt: fetchLastShared, refresh: refreshFetchUsage } = useFetchUsage();
  const fetchUsage = { used: fetchUsedShared, limit: fetchLimitShared, remaining: fetchRemainingShared };
  const [lastFetchTime, setLastFetchTime]   = useState(null);
  const [showRoasSettings, setShowRoasSettings] = useState(false);
  const [roasConfig, setRoasConfig] = useState(() => {
    try { return JSON.parse(localStorage.getItem('rh_roas_config') || '{"publisher":true,"ecommerce":true}'); }
    catch { return { publisher: true, ecommerce: true }; }
  });

  const updateRoasConfig = (key, val) => {
    const next = { ...roasConfig, [key]: val };
    setRoasConfig(next);
    try { localStorage.setItem('rh_roas_config', JSON.stringify(next)); } catch {}
  };

  const today = new Date().toISOString().split('T')[0];
  const hour  = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const name  = profile?.username || profile?.email?.split('@')[0] || 'there';
  const dateStr = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  const loadAccounts = useCallback(async () => {
    if (!user?.id) return;
    try {
      const [
        { data: adsense }, { data: admanager },
        { data: meta }, { data: gads }, { data: shopify }, { data: tiktok },
      ] = await Promise.all([
        supabase.from('adsense_settings').select('is_connected,last_fetch').eq('user_id', user.id).maybeSingle(),
        supabase.from('admanager_settings').select('is_connected,network_code,last_fetch').eq('user_id', user.id).maybeSingle(),
        supabase.from('meta_accounts').select('id,is_connected,updated_at').eq('user_id', user.id).eq('is_connected', true),
        supabase.from('google_ads_accounts').select('id,is_active,updated_at').eq('user_id', user.id).eq('is_active', true),
        supabase.from('shopify_stores').select('id,status,last_fetch').eq('user_id', user.id).eq('status', 'active'),
        supabase.from('tiktok_accounts').select('id,is_connected,updated_at').eq('user_id', user.id).eq('is_connected', true),
      ]);
      setAccounts({
        adsense:   { connected: !!adsense?.is_connected, lastFetch: adsense?.last_fetch },
        admanager: { connected: !!admanager?.is_connected, name: admanager?.network_code ? `(${admanager.network_code})` : '', lastFetch: admanager?.last_fetch },
        metaAds:   { connected: (meta?.length||0)>0, count: meta?.length||0, lastFetch: meta?.[0]?.updated_at },
        googleAds: { connected: (gads?.length||0)>0, count: gads?.length||0, lastFetch: gads?.[0]?.updated_at },
        shopify:   { connected: (shopify?.length||0)>0, count: shopify?.length||0, lastFetch: shopify?.[0]?.last_fetch },
        tiktokAds: { connected: (tiktok?.length||0)>0, count: tiktok?.length||0, lastFetch: tiktok?.[0]?.updated_at },
      });

      // HapiGuard — query live from events tables (not daily_stats which needs cron)
      try {
        const now = new Date();
        const todayStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
        const fromTs = `${todayStr}T00:00:00Z`;
        const { data: hgSite } = await supabase
          .from('hapiguard_sites').select('id').eq('user_id', user.id).limit(1).maybeSingle();
        if (hgSite) {
          const [
            { count: pvCount },
            { count: clickCount },
            { count: blockedCount },
            { count: alertCount },
          ] = await Promise.all([
            supabase.from('hapiguard_page_views')
              .select('id', { count: 'exact', head: true })
              .eq('site_id', hgSite.id).gte('created_at', fromTs),
            supabase.from('hapiguard_click_events')
              .select('id', { count: 'exact', head: true })
              .eq('site_id', hgSite.id).gte('created_at', fromTs),
            supabase.from('hapiguard_click_events')
              .select('id', { count: 'exact', head: true })
              .eq('site_id', hgSite.id).eq('blocked', true).gte('created_at', fromTs),
            supabase.from('hapiguard_anomaly_alerts')
              .select('id', { count: 'exact', head: true })
              .eq('site_id', hgSite.id).eq('acknowledged', false),
          ]);
          setHgStats({
            connected:    true,
            pageViews:    pvCount    || 0,
            totalClicks:  clickCount || 0,
            blocked:      blockedCount || 0,
            unreadAlerts: alertCount || 0,
          });
        } else {
          setHgStats({ connected: false });
        }
      } catch { setHgStats({ connected: false }); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [user?.id]);

  const loadAnnouncements = useCallback(async () => {
    try {
      const { data } = await supabase.from('admin_announcements')
        .select('*').eq('is_active', true).eq('show_modal', true)
        .or('delivery.eq.revhapi_app,delivery.eq.both,delivery.is.null')
        .order('created_at', { ascending: false }).limit(3);
      setAnnouncements(data || []);
    } catch {}
  }, []);

  // loadFetchUsage replaced by useFetchUsage hook — mirror its lastFetchAt
  // into the legacy lastFetchTime state so the existing JSX keeps working.
  const loadFetchUsage = refreshFetchUsage;
  useEffect(() => {
    if (fetchLastShared) setLastFetchTime(new Date(fetchLastShared));
  }, [fetchLastShared]);

  const loadTodayRoas = useCallback(async () => {
    if (!user?.id) return;
    try {
      const sevenDaysAgo = new Date(); sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);
      const rangeStart = sevenDaysAgo.toISOString().split('T')[0];
      const [
        { data: adsenseToday }, { data: admanagerToday },
        { data: shopifyStores }, { count: lpCount }, { count: testCount },
        { data: adsenseTrend }, { data: admanagerTrend },
      ] = await Promise.all([
        supabase.from('adsense_daily').select('net_revenue').eq('user_id', user.id).eq('date', today),
        supabase.from('admanager_daily').select('net_revenue').eq('user_id', user.id).eq('date', today),
        supabase.from('shopify_stores').select('id').eq('user_id', user.id).eq('status', 'active').limit(1),
        supabase.from('landing_pages').select('id', { count: 'exact', head: true }).eq('user_id', user.id).eq('status', 'published'),
        supabase.from('ab_tests').select('id', { count: 'exact', head: true }).eq('user_id', user.id).in('status', ['active', 'running']),
        supabase.from('adsense_daily').select('date,net_revenue').eq('user_id', user.id).gte('date', rangeStart).order('date'),
        supabase.from('admanager_daily').select('date,net_revenue').eq('user_id', user.id).gte('date', rangeStart).order('date'),
      ]);

      const pubRevenue = (adsenseToday||[]).reduce((s,r)=>s+parseFloat(r.net_revenue||0),0)
                       + (admanagerToday||[]).reduce((s,r)=>s+parseFloat(r.net_revenue||0),0);
      setActiveLandingPages(lpCount || 0);
      setActiveTests(testCount || 0);

      const dateMap = {};
      for (let i = 6; i >= 0; i--) {
        const d = new Date(); d.setDate(d.getDate() - i);
        const k = d.toISOString().split('T')[0];
        dateMap[k] = { date: k.slice(5), publisher: 0, ecommerce: 0 };
      }
      (adsenseTrend||[]).forEach(r => { if (dateMap[r.date]) dateMap[r.date].publisher += parseFloat(r.net_revenue||0); });
      (admanagerTrend||[]).forEach(r => { if (dateMap[r.date]) dateMap[r.date].publisher += parseFloat(r.net_revenue||0); });

      let ecomRevenue = 0, ecomSpend = 0;
      if (shopifyStores?.length) {
        const storeId = shopifyStores[0].id;
        const [{ data: ecomToday }, { data: ecomTrend }] = await Promise.all([
          supabase.from('shopify_daily_analytics').select('revenue,ad_spend').eq('user_id', user.id).eq('store_id', storeId).eq('date', today).maybeSingle(),
          supabase.from('shopify_daily_analytics').select('date,revenue').eq('user_id', user.id).eq('store_id', storeId).gte('date', rangeStart).order('date'),
        ]);
        ecomRevenue = parseFloat(ecomToday?.revenue || 0);
        ecomSpend   = parseFloat(ecomToday?.ad_spend || 0);
        (ecomTrend||[]).forEach(r => { if (dateMap[r.date]) dateMap[r.date].ecommerce = parseFloat(r.revenue||0); });
      }

      setTrendData(Object.values(dateMap));
      setTodayRoas({
        publisher: { revenue: pubRevenue },
        ecommerce: { revenue: ecomRevenue, spend: ecomSpend, roas: ecomSpend > 0 ? ecomRevenue / ecomSpend : 0 },
      });
    } catch (e) { console.error(e); }
  }, [user?.id, today]);

  useEffect(() => {
    loadAccounts(); loadAnnouncements(); loadTodayRoas(); loadFetchUsage();
  }, [loadAccounts, loadAnnouncements, loadTodayRoas, loadFetchUsage]);

  // Re-fetch all dashboard data when user returns to this tab after being away
  useVisibilityRefresh(useCallback(() => {
    loadAccounts(); loadAnnouncements(); loadTodayRoas(); loadFetchUsage();
  }, [loadAccounts, loadAnnouncements, loadTodayRoas, loadFetchUsage]));

  const handleFetchAll = async () => {
    if (fetchingAll) return;
    setFetchingAll(true);
    setFetchProgress({ label: 'Starting...' });
    const tid = toast.loading('Fetching all data...');
    try {
      const platforms = [];
      if (accounts.adsense.connected)   platforms.push('adsense');
      if (accounts.admanager.connected) platforms.push('admanager');
      if (accounts.metaAds.connected)   platforms.push('meta_ads');
      if (accounts.googleAds.connected) platforms.push('google_ads');
      if (!platforms.length) { toast.error('No connected platforms', { id: tid }); return; }
      setFetchProgress({ label: `Fetching ${platforms.length} platform${platforms.length > 1 ? 's' : ''}...` });
      const res  = await authFetch('/api/fetch-all-data', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ userId: user.id }) });
      const data = await res.json();
      if (data.success) {
        toast.success('All data updated!', { id: tid });
        loadAccounts(); loadTodayRoas(); loadFetchUsage();
      } else toast.error(data.error || 'Failed', { id: tid });
    } catch { toast.error('Failed', { id: tid }); }
    finally { setFetchingAll(false); setFetchProgress(null); }
  };

  const ACCOUNT_LIST = [
    { key: 'adsense',   label: 'AdSense',    icon: '💰', href: '/adsense',    category: 'Publisher', connected: accounts.adsense.connected,   lastFetch: accounts.adsense.lastFetch },
    { key: 'admanager', label: 'Ad Manager', icon: '📊', href: '/ad-manager', category: 'Publisher', connected: accounts.admanager.connected, lastFetch: accounts.admanager.lastFetch, detail: accounts.admanager.name },
    { key: 'metaAds',   label: 'Meta Ads',   icon: '📘', href: '/meta-ads',   category: 'Ad Spend',  connected: accounts.metaAds.connected,   lastFetch: accounts.metaAds.lastFetch,   detail: accounts.metaAds.count > 1 ? `${accounts.metaAds.count} accounts` : '' },
    { key: 'googleAds', label: 'Google Ads', icon: '🎯', href: '/google-ads', category: 'Ad Spend',  connected: accounts.googleAds.connected, lastFetch: accounts.googleAds.lastFetch, detail: accounts.googleAds.count > 1 ? `${accounts.googleAds.count} accounts` : '' },
    { key: 'tiktokAds', label: 'TikTok Ads', icon: '🎵', href: '/tiktok-ads', category: 'Ad Spend',  connected: accounts.tiktokAds.connected, lastFetch: accounts.tiktokAds.lastFetch, detail: accounts.tiktokAds.count > 1 ? `${accounts.tiktokAds.count} accounts` : '' },
    { key: 'shopify',   label: 'Shopify',    icon: '🛍️', href: '/shopify',    category: 'Ecommerce', connected: accounts.shopify.connected,   lastFetch: accounts.shopify.lastFetch,   detail: accounts.shopify.count > 1 ? `${accounts.shopify.count} stores` : '' },
  ];

  const connectedCount = ACCOUNT_LIST.filter(a => a.connected).length;
  const usagePct = fetchUsage.limit > 0 ? (fetchUsage.used / fetchUsage.limit) * 100 : 0;
  const hasTrend = trendData.some(d => d.publisher > 0 || d.ecommerce > 0);
  const fmtTime  = (d) => d ? new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : null;

  if (loading) return <SkeletonDashboard />;

  return (
    <div className="space-y-6 pb-6">

      {/* ── Hero greeting ── */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 px-6 py-8 text-white shadow-xl">
        <div className="pointer-events-none absolute -top-16 -right-16 h-48 w-48 rounded-full bg-emerald-500/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-10 -left-10 h-40 w-40 rounded-full bg-teal-500/15 blur-3xl" />

        <div className="relative flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-emerald-400 mb-1">{dateStr}</p>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
              {greeting}, <span className="text-emerald-400 capitalize">{name}</span> 👋
            </h1>
            <p className="text-sm text-muted-foreground/70 mt-1">
              {connectedCount}/{ACCOUNT_LIST.length} platforms connected
            </p>
          </div>
          <button
            onClick={handleFetchAll}
            disabled={fetchingAll || connectedCount === 0}
            className="flex items-center gap-2 self-start px-4 py-2.5 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-white font-semibold rounded-xl text-sm transition-colors shadow-lg shadow-emerald-500/25"
          >
            <RefreshCw className={`h-4 w-4 ${fetchingAll ? 'animate-spin' : ''}`} />
            {fetchProgress ? fetchProgress.label : 'Fetch All'}
          </button>
        </div>

        {/* Fetch usage */}
        <div className="relative mt-6 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs text-muted-foreground/70 flex items-center gap-1"><Database className="h-3 w-3" /> Daily fetches</span>
              <span className="text-xs font-semibold text-white">{fetchUsage.used} / {fetchUsage.limit}</span>
            </div>
            <div className="h-1.5 rounded-full bg-card/10">
              <div
                className={`h-1.5 rounded-full transition-all ${usagePct >= 100 ? 'bg-red-400' : usagePct >= 70 ? 'bg-amber-400' : 'bg-emerald-400'}`}
                style={{ width: `${Math.min(100, usagePct)}%` }}
              />
            </div>
          </div>
          <div className="flex items-center gap-4 text-xs text-muted-foreground/70 flex-shrink-0">
            {lastFetchTime && (
              <span className="flex items-center gap-1.5">
                <Clock className="h-3 w-3" />
                Last sync {lastFetchTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
            {fetchUsage.remaining <= 0 && <span className="text-red-400 font-semibold">Limit reached</span>}
            {!isPro && fetchUsage.remaining <= 2 && fetchUsage.remaining > 0 && (
              <Link to="/subscribe" className="text-emerald-400 hover:text-emerald-300 font-semibold">Upgrade →</Link>
            )}
          </div>
        </div>
      </div>

      {/* ── Performance metrics ── */}
      <div>
        <div className="flex items-center justify-between mb-3 relative">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Today's performance</p>
          <button onClick={() => setShowRoasSettings(v => !v)}
            className="flex items-center gap-1.5 text-xs text-muted-foreground/70 hover:text-foreground/80 px-2.5 py-1.5 rounded-lg hover:bg-muted transition-colors">
            <Settings className="h-3.5 w-3.5" /> Configure
          </button>
          {showRoasSettings && (
            <div className="absolute right-0 top-8 z-30 w-56 bg-card border border-border rounded-2xl shadow-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-bold">Show cards</p>
                <button onClick={() => setShowRoasSettings(false)}><X className="h-4 w-4 text-muted-foreground/70" /></button>
              </div>
              {[{ key: 'publisher', label: 'Publisher ROAS', icon: '📊' }, { key: 'ecommerce', label: 'Ecommerce ROAS', icon: '🛍️' }].map(({ key, label, icon }) => (
                <label key={key} className="flex items-center justify-between cursor-pointer">
                  <span className="flex items-center gap-2 text-sm">{icon} {label}</span>
                  <div onClick={() => updateRoasConfig(key, !roasConfig[key])}
                    className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${roasConfig[key] ? 'bg-emerald-500' : 'bg-muted'}`}>
                    <span className={`absolute top-0.5 w-4 h-4 bg-card rounded-full shadow transition-all ${roasConfig[key] ? 'left-5' : 'left-0.5'}`} />
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 items-stretch">
          {roasConfig.publisher !== false && (
            <MetricCard
              href="/publisher"
              cta="Go to Publisher"
              label="Publisher Revenue"
              value={`$${todayRoas.publisher.revenue.toFixed(2)}`}
              sub="Today · AdSense + Ad Manager"
              gradient="from-emerald-500 to-teal-600"
              icon={BarChart3}
            />
          )}
          {roasConfig.ecommerce !== false && (
            <MetricCard
              href="/ecommerce"
              cta="Go to Ecommerce"
              label="Ecommerce ROAS"
              value={todayRoas.ecommerce.spend > 0 ? `${todayRoas.ecommerce.roas.toFixed(2)}x` : '—'}
              sub={todayRoas.ecommerce.spend > 0 ? `$${todayRoas.ecommerce.revenue.toFixed(0)} rev · $${todayRoas.ecommerce.spend.toFixed(0)} spend` : 'No spend linked'}
              gradient="from-blue-500 to-indigo-600"
              icon={ShoppingCart}
            />
          )}
          <MetricCard
            href="/landing-pages"
            cta="Go to Landing Pages"
            label="Live Pages"
            value={activeLandingPages.toString()}
            sub="Published landing pages"
            gradient="from-purple-500 to-violet-600"
            icon={Activity}
          />
          <MetricCard
            href="/ab-tests"
            cta="Go to A/B Tests"
            label="Active Tests"
            value={activeTests.toString()}
            sub="Running A/B experiments"
            gradient="from-orange-500 to-amber-500"
            icon={FlaskConical}
            badge={activeTests > 0 ? 'Running' : undefined}
          />
          <MetricCard
            href="/hapiguard"
            cta="Go to HapiGuard"
            label={hgStats?.connected ? "Blocked Today" : "HapiGuard"}
            value={hgStats?.connected ? (hgStats.blocked || 0).toLocaleString() : "Set Up"}
            sub={hgStats?.connected
              ? `${(hgStats.pageViews||0).toLocaleString()} page views · ${(hgStats.totalClicks||0)} clicks`
              : "Protect your ad revenue from invalid clicks"}
            gradient="from-red-500 to-rose-600"
            icon={ShieldCheck}
            badge={hgStats?.unreadAlerts > 0 ? `${hgStats.unreadAlerts} alert${hgStats.unreadAlerts>1?'s':''}` : undefined}
          />
        </div>
      </div>

      {/* ── 7-day Trend Chart ── */}
      {hasTrend && (
        <div className={`${card} p-5`}>
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-sm font-bold text-foreground">7-Day Revenue Trend</h2>
              <p className="text-xs text-muted-foreground/70 mt-0.5">Publisher + Ecommerce</p>
            </div>
            <TrendingUp className="h-5 w-5 text-emerald-500" />
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={trendData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="gPub" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gEcom" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false}
                tickFormatter={v => `$${v >= 1000 ? (v/1000).toFixed(0)+'k' : v.toFixed(0)}`} width={45} />
              <Tooltip content={<ChartTip />} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="publisher" name="Publisher" stroke="#10b981" strokeWidth={2} fill="url(#gPub)" dot={false} />
              <Area type="monotone" dataKey="ecommerce" name="Ecommerce" stroke="#3b82f6" strokeWidth={2} fill="url(#gEcom)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ── Trial banner ── */}
      {isTrialActive && daysRemaining <= 7 && (
        <div className="flex items-center gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-2xl">
          <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0" />
          <p className="text-sm text-amber-800 dark:text-amber-200 flex-1">Trial ends in <strong>{daysRemaining} day{daysRemaining !== 1 ? 's' : ''}</strong>.</p>
          <Link to="/subscribe" className="text-sm font-semibold text-amber-700 hover:text-amber-800 flex items-center gap-1">
            Upgrade <ChevronRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      )}

      {/* ── Announcements ── */}
      {announcements.length > 0 && (
        <div className="space-y-2">
          {announcements.map(a => (
            <div key={a.id} className="flex items-start gap-3 p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800 rounded-2xl">
              <Megaphone className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-foreground">{a.title}</p>
                {a.message && <p className="text-sm text-muted-foreground mt-0.5">{a.message}</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Connected accounts ── */}
      <div className={card}>
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-border/40">
          <div>
            <h2 className="text-sm font-bold text-foreground flex items-center gap-2">
              <Database className="h-4 w-4 text-emerald-500" /> Connected Platforms
            </h2>
            <p className="text-xs text-muted-foreground/70 mt-0.5">{connectedCount} of {ACCOUNT_LIST.length} active</p>
          </div>
        </div>
        <div className="divide-y divide-border/60">
          {ACCOUNT_LIST.map(acc => (
            <div key={acc.key} className="flex items-center gap-4 px-5 py-3.5 hover:bg-muted/40 transition-colors">
              <span className="text-xl w-8 text-center flex-shrink-0">{acc.icon}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-foreground">{acc.label}</p>
                  <span className="text-xs text-muted-foreground/70">{acc.category}</span>
                  {acc.detail && <span className="text-xs text-muted-foreground/70">· {acc.detail}</span>}
                </div>
                {acc.connected && acc.lastFetch && (
                  <p className="text-xs text-muted-foreground/70 flex items-center gap-1 mt-0.5">
                    <Clock className="h-3 w-3" /> {fmtTime(acc.lastFetch)}
                  </p>
                )}
              </div>
              <StatusDot connected={acc.connected} />
              <Link to={acc.href} className="flex items-center gap-1 text-xs font-semibold text-emerald-600 hover:text-emerald-700 flex-shrink-0">
                {acc.connected ? 'Manage' : 'Connect'} <ChevronRight className="h-3 w-3" />
              </Link>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}
